<template>
  <div>
    <v-data-table
      :headers="getHeaderList()"
      :items="entityList"
      :loading="loading"
      class="transparent"
      :data-cy="`${entityName}List-table`"
      disable-pagination
      hide-default-footer
      v-if="isCurrentRoute"
    >
      <template v-slot:top>
        <div class="mx-3 d-flex">
          <v-combobox
            :items="attributeList"
            clearable
            :data-cy="`${entityName}List-columnSelectorDropdown`"
            dense
            item-text="label"
            item-value="name"
            label="Show Columns"
            multiple
            outlined
            v-model="attributeListSelected"
          ></v-combobox>
          <v-spacer></v-spacer>
          <v-btn
            class="ml-1"
            color="primary"
            :data-cy="`${entityName}List-create`"
            outlined
            :to="`${url}/create`"
            v-if="getPermission('create')"
            >Add</v-btn
          >
        </div>
      </template>

      <template v-slot:item.actionList="{ item }">
        <v-row justify="end" style="min-width: 200px">
          <v-btn
            :data-cy="`${entityName}List-${item.name}-updateBtn`"
            :to="`${url}/${item[primaryAttributeName]}`"
            class="ma-1"
            color="primary"
            outlined
            small
            v-if="getPermission('update')"
            >Update</v-btn
          >
          <div v-if="!item.locked && getPermission('remove')">
            <attribute-delete-dialog
              v-if="isUserOrResourceAttribute"
              :attributeEntityName="entityName"
              :attributeName="item[primaryAttributeName]"
              :data-cy="`${entityName}List-${item.name}-deleteBtn`"
              :parentEntityName="parentEntityName"
              class="ma-1"
            ></attribute-delete-dialog>
            <delete-dialog
              :entityType="entityName"
              :entityId="item[primaryAttributeName]"
              :data-cy="`${entityName}List-${item.name}-deleteBtn`"
              class="ma-1"
              v-else
            ></delete-dialog>
          </div>
          <template v-else>
            <div class="pl-12 ml-9"></div>
          </template>
        </v-row>
      </template>

      <template v-slot:item.associationList="{ item }">
        <div v-for="(association, index) in associationList" :key="index">
          <v-btn
            class="mr-2"
            color="primary"
            outlined
            small
            :to="
              assign({}, association.to, {
                params: { [primaryAttributeName]: item[primaryAttributeName] },
              })
            "
            >{{ association.label }}</v-btn
          >
        </div>
      </template>

      <template v-slot:no-data>
        <i>No data</i>
      </template>
    </v-data-table>

    <router-view></router-view>
  </div>
</template>

<script>
import { assign, get } from "lodash";
import { mapActions, mapGetters } from "vuex";

import AttributeDeleteDialog from "@/components/admin/AttributeDeleteDialog.vue";
import DeleteDialog from "@/components/DeleteDialog";

export default {
  components: {
    AttributeDeleteDialog,
    DeleteDialog,
  },
  computed: {
    attributeList() {
      let attributeList = this.attributeGetByAttributeValue(
        "entityName",
        this.entityName
      );

      attributeList.sort(({ orderNumber: first }, { orderNumber: second }) => {
        if (first === undefined && second === undefined) {
          return 0;
        }

        if (first === undefined) {
          return 1;
        }

        if (second === undefined) {
          return -1;
        }

        if (first === second) {
          return 0;
        }

        return first < second ? -1 : 1;
      });

      return attributeList;
    },
    isCurrentRoute() {
      const {
        $route: { name: currentRouteName },
        routeName,
      } = this;

      return currentRouteName === routeName;
    },
    isUserOrResourceAttribute() {
      // This checks if the admin entity is a User or Resource.
      const isUserOrResourceAttribute =
        this.entityName === "userAttribute" ||
        this.entityName === "resourceAttribute"
          ? true
          : false;
      return isUserOrResourceAttribute;
    },
    entityList() {
      const entityStore = this.$store.state[this.entityName];

      if (!entityStore) {
        throw new Error(`No store found for entityName "${this.entityName}"`);
      }

      let { list } = entityStore;

      list = list.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));

      return list;
    },
    permission() {
      return this.$store.state.session.user.permission[this.entityName] || {};
    },
    primaryAttributeName() {
      const primaryAttribute = this.attributeList.find(
        attribute => !!attribute.primaryKey
      );

      return get(primaryAttribute, "name");
    },
    url() {
      return this.$route.path;
    },
    ...mapGetters({
      attributeGetByAttributeValue: "attribute/filterByAttributeValue",
    }),
  },
  async created() {
    this.loading = true;
    await Promise.all([
      this.attributeGetList({ reload: false }),
      this.entityGetList({ reload: false }),
    ]);

    this.attributeListSelected = this.attributeList.filter(
      attribute => attribute.showDefault
    );
    this.loading = false;
  },
  data() {
    return {
      attributeListSelected: [],
      attributeMap: {},
      dialog: false,
      id: undefined,
      loading: false,
      mode: undefined,
    };
  },
  methods: {
    assign,
    async entityCreate(attributeMap) {
      return this.$store.dispatch(`${this.entityName}/create`, attributeMap);
    },
    async entityGetList() {
      return this.$store.dispatch(`${this.entityName}/list`);
    },
    async entityUpdate([id, attributeMap]) {
      return this.$store.dispatch(`${this.entityName}/update`, [
        id,
        attributeMap,
      ]);
    },
    getHeaderList() {
      let additionalHeaderList = [
        {
          text: "",
          value: "actionList",
        },
      ];

      if (this.associationList) {
        additionalHeaderList = additionalHeaderList.concat({
          text: "Associations",
          value: "associationList",
        });
      }

      const headerList = this.attributeListSelected
        .map(({ label: text, name: value }) => ({ text, value }))
        .concat(additionalHeaderList);

      return headerList;
    },
    getPermission(action) {
      const actionValue = this.permission[action];
      if (actionValue === undefined) return true;
      return actionValue;
    },
    ...mapActions({
      attributeGetList: "attribute/list",
    }),
  },
  props: {
    associationList: {
      type: Array,
    },
    entityName: {
      required: true,
      type: String,
    },
    filterByAttribute: {
      type: String,
    },
    filterByAttributeValue: {},
    routeName: {
      type: String,
      required: true,
    },
    parentEntityName: {},
  },
  watch: {
    entityName: async function () {
      await this.entityGetList();

      this.attributeListSelected = this.attributeList.filter(
        attribute => attribute.requiredCreate
      );
    },
  },
};
</script>
